import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

const API = process.env.REACT_APP_API_URL;
const FRONTEND = (process.env.REACT_APP_FRONTEND_URL || window.location.origin).replace(/\/$/, '');

const statusColors = {
  available: 'bg-blue-100 text-blue-700',
  linked: 'bg-green-100 text-green-700',
  retired: 'bg-gray-100 text-gray-500',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[status] || 'bg-gray-100 text-gray-500'}`}>
      {status || '—'}
    </span>
  );
}

function Modal({ open, onClose, title, children, footer, maxWidth = 'max-w-sm' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-xl w-full ${maxWidth} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">{footer}</div>
        )}
      </div>
    </div>
  );
}

const inputCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const btnPrimary = 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const btnOutline = 'border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors';

export default function QrPool() {
  const { userInfo } = useSelector(state => state.userLogin || {});
  const [status, setStatus] = useState('available');
  const [count, setCount] = useState('5');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const authConfig = {
    headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {},
  };

  const load = async () => {
    try {
      setErr('');
      setLoading(true);
      const { data } = await axios.get(`${API}/qrs`, {
        ...authConfig,
        params: { status: status || undefined },
      });
      setItems(data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Failed to load QRs');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [status, userInfo?.token]);

  const generate = async () => {
    const n = Number(count || 0);
    if (!Number.isFinite(n) || n <= 0) return;
    try {
      setCreating(true);
      await axios.post(`${API}/qrs`, { count: n, withPng: false }, authConfig);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Failed to generate QRs');
    } finally {
      setCreating(false);
    }
  };

  const downloadQr = async (token, number) => {
    try {
      setErr('');
      const res = await axios.get(`${API}/qrs/${token}/png`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${number}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Failed to download QR');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      it.number?.toString().includes(q) ||
      it.token?.toLowerCase().includes(q) ||
      it.merchant?.name?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const rows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">QR Pool</h1>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <select
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(0); }}
          className={inputCls}
        >
          <option value="">All Status</option>
          <option value="available">Available</option>
          <option value="linked">Linked</option>
          <option value="retired">Retired</option>
        </select>

        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search QR / Token / Merchant"
          className={`${inputCls} flex-1 min-w-[200px]`}
        />

        <input
          value={count}
          onChange={e => setCount(e.target.value.replace(/\D/g, ''))}
          placeholder="Count"
          className={`${inputCls} w-24`}
        />

        <button onClick={generate} disabled={creating || !Number(count)} className={btnPrimary}>
          {creating ? 'Creating…' : `Generate${Number(count) ? ` (${count})` : ''}`}
        </button>
      </div>

      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    {['#', 'QR Code', 'QR Number', 'Token', 'Status', 'Merchant', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                        No QR codes found
                      </td>
                    </tr>
                  ) : rows.map((it, idx) => (
                    <tr key={it.token} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-400">{page * rowsPerPage + idx + 1}</td>
                      <td className="px-4 py-3">
                        <img
                          src={`${API}/qrs/${it.token}/png`}
                          alt="QR"
                          className="w-14 h-14 border border-gray-200 rounded object-contain"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-800">{it.number}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-gray-500 block max-w-[180px] truncate" title={it.token}>
                          {it.token}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={it.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{it.merchant?.name || '—'}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setPreview(it)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium border border-blue-200 hover:border-blue-400 px-3 py-1 rounded-lg transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
                <select
                  value={rowsPerPage}
                  onChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {[10, 25, 50].map(n => <option key={n} value={n}>{n}/page</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                  Prev
                </button>
                <span className="text-sm text-gray-600 px-1">{page + 1} / {Math.max(1, totalPages)}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Preview Modal */}
      <Modal
        open={!!preview}
        onClose={() => setPreview(null)}
        title="QR Preview"
        footer={
          preview && (
            <>
              <button onClick={() => window.open(`${FRONTEND}/form/${preview.token}`, '_blank')} className={btnPrimary}>
                Open Form
              </button>
              <button onClick={() => downloadQr(preview.token, preview.number)} className={btnOutline}>
                Download
              </button>
              <button onClick={() => setPreview(null)} className={btnOutline}>
                Close
              </button>
            </>
          )
        }
      >
        {preview && (
          <div className="text-center">
            <img
              src={`${API}/qrs/${preview.token}/png`}
              alt="QR"
              className="w-48 h-48 border border-gray-200 rounded-lg mx-auto object-contain"
            />
            <div className="mt-4 space-y-1 text-sm text-gray-700 text-left">
              <p><span className="font-semibold">QR No:</span> {preview.number}</p>
              <p><span className="font-semibold">Status:</span> <StatusBadge status={preview.status} /></p>
              <p><span className="font-semibold">Merchant:</span> {preview.merchant?.name || '—'}</p>
            </div>
            <p className="mt-3 text-xs font-mono text-gray-400 break-all">{preview.token}</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
